import { Entity } from '@/models/types/Entity';
import { BusinessEntity, Notification, Room, Roomgroup } from '@/models';
import {
  apiErrorCodes,
  entityTypes,
  ImpedimentType,
  ImpedimentReason,
  impedimentReasonLabels,
} from '@/enums/generic';
import { useBusinessEntityStore } from '@/store/entities/businessEntityStore';
import { useRoomStore } from '@/store/entities/roomStore';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { useBuildingStore } from '@/store/entities/buildingStore';
import {
  getAllBusinessEntities,
  getBusinessEntityById,
} from '@/api/businessEntity/BusinessEntityApi';
import classTransformer from '@/utils/transformers/classTransformer';
import { useRadiatorStore } from '@/store/entities/radiatorStore';
import { getAllEntitiesByCriteria } from '@/store/entities/helper';
import { useNotificationStore } from '@/store/notifications/notificationStore';
import { useFormStore } from '@/store/form/formStore';
import { usageTypeList } from '@/enums/roomgroup';
import { Pinia, Store } from '@/store/pinia-class-component';
import { useLayoutStore } from '@/store/layout/layoutStore';
import { miniEntityStore } from '@/store/entities/types/miniEntityStore';
import { useInstallationPointStore } from '@/store/entities/installationPointStore';
import { useOrderStore } from '@/store/order/orderStore';
import { useImagesStore } from '@/store/images/imageStore';
import { usePlantStore } from '@/store/entities/plantStore';
import { useInspectionPointStore } from '@/store/entities/inspectionPointStore';
import { plainToInstance } from 'class-transformer';
import { useInspectedBuildingStore } from '@/store/entities/inspectedBuildingStore';
import { useInspectionDeviceStore } from '@/store/entities/inspectionDeviceStore';
import { usePersistenceStore } from '@/store/persistence/persistenceStore';
import { ProductEstimationTypesEnmu } from '@/enums/device';
import { generateUniqueId } from '@/helpers/uuidUtils';
import { useInspectedBuildingForm } from '@/composables/useInspectedBuildingForm';
import { InspectionPoint } from '@/models/inspectionPoint/InspectionPoint';
import { ProductEstimation } from '@/models/InspectedBuilding';
import { EntityTransactionType } from '@/models/OrderChanges';
import { getAnalyticsProvider } from '@/plugins/bxAnalytics';
import { AnalyticsProvider } from '@/plugins/bxAnalytics/types/AnalyticsProvider';
import { useDmgw6FunkcheckSync } from '@/composables/useDmgw6FunkcheckSync';

type id = string;
type BusinessEntityMap = Map<id, Entity>;
type EntityCrudPayload = {
  suppressEntityCrudEvent?: boolean;
};

/*
 * Reasons the backend's PHX-2408 migration script types as TEMPORARY; every
 * other reason becomes PERMANENT. Kept in sync with that script so a tablet
 * migrating stale localStorage derives the same type the server already did.
 */
const LEGACY_TEMPORARY_REASONS = [
  'Passendes Werkzeug fehlt',
  'keinen passenden Adapter dabei',
  'kein Gerät dabei',
];

const LEGACY_REASON_TO_ENUM: Record<string, ImpedimentReason> =
  Object.fromEntries([
    ...Object.entries(impedimentReasonLabels).map(([k, v]) => [v, k]),
    ...Object.values(ImpedimentReason).map((v) => [v, v]),
  ]) as Record<string, ImpedimentReason>;

export function useEntitiesStore() {
  return new EntitiesStore();
}

@Store
export class EntitiesStore extends Pinia {
  private _activeBusinessEntityId?: id;
  private _businessEntities: Map<id, BusinessEntityMap>;

  constructor() {
    super();
    this._activeBusinessEntityId = undefined;
    this._businessEntities = new Map();
  }

  private getAnalytics(): AnalyticsProvider | undefined {
    return getAnalyticsProvider();
  }

  private isTrackedEntityCrudType(entity: Entity) {
    return (
      entity.type === entityTypes.building ||
      entity.type === entityTypes.roomgroup
    );
  }

  private trackEntityCrudEvent(
    entity: Entity,
    action: EntityTransactionType,
    payload?: EntityCrudPayload
  ) {
    const analytics = this.getAnalytics();

    if (
      payload?.suppressEntityCrudEvent ||
      !analytics ||
      !this.isTrackedEntityCrudType(entity)
    ) {
      return;
    }

    const activeOrder = useOrderStore().activeOrder;
    const eventPayload = {
      entity_type: entity.type,
      action,
      source: 'installer',
      entity_id: entity.id,
      order_id: activeOrder?.id,
      order_number: activeOrder?.orderId,
    };

    analytics.captureEvent('entity_crud', eventPayload);
  }

  get activeBusinessEntityId(): id | undefined {
    return this._activeBusinessEntityId;
  }

  get activeBusinessEntity(): BusinessEntity | undefined {
    if (!this._activeBusinessEntityId) {
      return;
    }

    return this._businessEntities
      .get(this._activeBusinessEntityId)
      ?.values()
      ?.next().value as BusinessEntity | undefined;
  }

  getBusinessEntityById(businessEntityId: id): BusinessEntityMap | undefined {
    return this._businessEntities.get(businessEntityId);
  }

  get businessEntities(): Map<id, BusinessEntityMap> {
    return this._businessEntities;
  }

  get businessEntityMap() {
    const businessEntity = new Map();
    businessEntity.set(
      this.getStores().businessEntityStore.businessEntity?.id,
      this.getStores().businessEntityStore.businessEntity
    );

    const array = [
      ...businessEntity,
      ...this.getStores().buildingStore.buildings,
      ...this.getStores().inspectedBuildingStore.inspectedBuildings,
      ...this.getStores().roomGroupStore.roomGroups,
      ...this.getStores().roomStore.rooms,
      ...this.getStores().deviceStore.devices,
      ...this.getStores().inspectionDeviceStore.inspectionDevices,
      ...this.getStores().radiatorStore.radiators,
      ...this.getStores().installationPointStore.installationPoints,
      ...this.getStores().inspectionPointStore.inspectionPoints,
      ...this.getStores().plantStore.plants,
    ];

    return new Map(array);
  }

  hasBusinessEntity(businessEntityId: id): boolean {
    const businessEntity = this.getBusinessEntityById(businessEntityId);
    return businessEntity !== undefined && businessEntity.size > 1;
  }

  fetchAllBusinessEntities() {
    return getAllBusinessEntities()
      .then(async (resp) => {
        for (const entity of resp.data) {
          this.setBusinessEntityList(
            classTransformer.plainToClassEntity(entity)
          );
        }
        return resp;
      })
      .catch((error) => {
        console.error(error);
      });
  }

  resetActiveBusinessEntity() {
    this._activeBusinessEntityId = undefined;
  }

  setActiveBusinessEntityById(businessEntityId: id) {
    const layoutStore = useLayoutStore();
    layoutStore.setLoadingState(true);
    const notificationStore = useNotificationStore();
    this._activeBusinessEntityId = businessEntityId;
    return this.loadBusinessEntity(businessEntityId)
      .then((businessEntityMap: any) => {
        this.setBusinessEntityList(businessEntityMap);
        this.resetMiniEntityStores();
        this.setBusinessEntityMap(businessEntityMap);
        useDmgw6FunkcheckSync().hydrateFromInstallationPoints();

        if (
          useOrderStore().isOnSiteInspection() &&
          useBuildingStore().buildings &&
          useBuildingStore().buildings.size > 0 &&
          this.activeBusinessEntity
        ) {
          this.activeBusinessEntity?.buildings.forEach((building) => {
            const existingInspectedBuilding =
              this.activeBusinessEntity?.inspectedBuildings.find(
                (inspectedBuilding) =>
                  inspectedBuilding.buildingId === building.id
              );

            if (existingInspectedBuilding) {
              const legacySystemSRTs = (existingInspectedBuilding as any)
                .systemSRTs;
              const legacyEstimatedSRTs = (existingInspectedBuilding as any)
                .estimatedSRTs;
              if (legacySystemSRTs !== undefined) {
                existingInspectedBuilding.productEstimations.push({
                  id: generateUniqueId(),
                  type: 'SRT',
                  customizedNumber: '42001',
                  count: legacySystemSRTs,
                  estimation: legacyEstimatedSRTs,
                });
              }
            } else {
              const { inspectedBuilding, initializeInspectedBuildingForm } =
                useInspectedBuildingForm(undefined, building.id);
              initializeInspectedBuildingForm();
              this.activeBusinessEntity?.inspectedBuildings.push(
                inspectedBuilding.value
              );
              useInspectedBuildingStore().addEntity(inspectedBuilding.value);
            }
          });

          this.syncBusinessEntities();
        }

        layoutStore.setLoadingState(false);
      })
      .catch((error) => {
        notificationStore.addNotification(
          new Notification(
            'Stell sicher, dass du mit dem Internet verbunden bist.',
            'Du bist offline!',
            'error'
          )
        );
        layoutStore.setLoadingState(false);
        return Promise.reject(error);
      });
  }

  async loadBusinessEntity(businessEntityId: id): Promise<BusinessEntityMap> {
    const layoutStore = useLayoutStore();
    const fromStore = this.getBusinessEntityById(businessEntityId);

    if (layoutStore.isOnline) {
      await this.getStores().imagesStore.getImagesConfirmation(
        businessEntityId
      );
    }

    try {
      if (fromStore && fromStore.size > 1) {
        console.debug('loadBusinessEntity: from store');
        this.migrateLegacyImpedimentsInMap(fromStore);
        return Promise.resolve(fromStore);
      }

      const fromLocalStorage =
        await this.loadBusinessEntityFromLocalStorage(businessEntityId);

      if (fromLocalStorage && fromLocalStorage.size > 1) {
        console.debug('loadBusinessEntity: from local storage');
        return Promise.resolve(fromLocalStorage);
      }
    } catch (error) {
      console.debug(error);
      console.debug("Local might be corrupted, let's try to fetch from server");
    }

    if (layoutStore.isOnline) {
      const fromAPI = await this.loadBusinessEntityFromAPI(businessEntityId);

      usePersistenceStore().saveBusinessEntityToLocal(fromAPI);

      const businessEntityMap: any[string][any] =
        classTransformer.flattenToMap(fromAPI);

      if (businessEntityMap) {
        return businessEntityMap;
      } else {
        console.debug('loadBusinessEntity: no data found');
        return Promise.reject(new Error('No data found'));
      }
    } else {
      return Promise.reject(new Error('No internet connection'));
    }
  }

  async loadBusinessEntityFromAPI(businessEntityId: id): Promise<any> {
    console.info('loadBusinessEntity: from api');
    return getBusinessEntityById(businessEntityId).then((resp) => {
      if (resp.data.code) {
        this.getStores().layoutStore.setLoadingState(false);
        const code: keyof typeof apiErrorCodes = resp.data.code;
        const apiErrorCode = apiErrorCodes[code];
        const notification = new Notification()
          .setType('error')
          .setText(apiErrorCode);

        this.getStores().notificationStore.addNotification(notification);
        throw new Error(resp.data.code);
      }

      return plainToInstance(BusinessEntity, resp.data);
    });
  }

  async loadBusinessEntityFromLocalStorage(
    businessEntityId: id
  ): Promise<BusinessEntityMap | undefined> {
    const localStorageBusinessEntity =
      await this.loadFromLocalStorage(businessEntityId);

    if (!localStorageBusinessEntity) {
      return undefined;
    }

    this.migrateLegacyImpediments(localStorageBusinessEntity);

    return classTransformer.plainToClassEntity(localStorageBusinessEntity);
  }

  // Data persisted before PHX-2110 has isAccessible === false without
  // typeNotAccessible or an impediments history; treat it as PERMANENT.
  migrateLegacyImpediments(businessEntity: any) {
    for (const key of ['buildings', 'roomGroups', 'rooms']) {
      for (const entity of businessEntity[key] ?? []) {
        this.migrateLegacyEntityImpediment(entity);
      }
    }

    for (const installationPoint of businessEntity.installationPoints ?? []) {
      this.migrateLegacyFailedAttempts(installationPoint);
    }
  }

  migrateLegacyImpedimentsInMap(businessEntityMap: BusinessEntityMap) {
    for (const entity of businessEntityMap.values()) {
      this.migrateLegacyEntityImpediment(entity);
      this.migrateLegacyFailedAttempts(entity);
    }
  }

  migrateLegacyEntityImpediment(entity: any) {
    if (
      entity.isAccessible === false &&
      (!entity.impediments || entity.impediments.length === 0)
    ) {
      entity.typeNotAccessible = ImpedimentType.PERMANENT;
      entity.impediments = [
        {
          id: generateUniqueId(),
          type: ImpedimentType.PERMANENT,
          reason: entity.reasonNotAccessible ?? '',
          recordedAt: entity.dateNotAccessible ?? new Date().toISOString(),
        },
      ];
    }
    this.migrateImpedimentReasonToEnum(entity);
  }

  migrateImpedimentReasonToEnum(entity: any) {
    if (entity.reasonNotAccessible) {
      entity.reasonNotAccessible =
        LEGACY_REASON_TO_ENUM[entity.reasonNotAccessible] ??
        ImpedimentReason.OTHER;
    }
    for (const impediment of entity.impediments ?? []) {
      if (impediment.reason) {
        impediment.reason =
          LEGACY_REASON_TO_ENUM[impediment.reason] ?? ImpedimentReason.OTHER;
      }
    }
  }

  // Failed installation attempts persisted before PHX-2408 have no type, while
  // the backend migration script has already derived one. A full-document PUT
  // would overwrite the server's value, so derive it here by the same rule.
  migrateLegacyFailedAttempts(installationPoint: any) {
    for (const attempt of installationPoint.failedInstallationAttempts ?? []) {
      this.migrateLegacyFailedAttempt(attempt);
    }
    this.migrateLegacyFailedAttempt(
      installationPoint.failedInstallationAttempt
    );
  }

  migrateLegacyFailedAttempt(attempt: any) {
    if (attempt && !attempt.type) {
      attempt.type = LEGACY_TEMPORARY_REASONS.includes(attempt.reason)
        ? ImpedimentType.TEMPORARY
        : ImpedimentType.PERMANENT;
    }
  }

  async loadFromLocalStorage(businessEntityId: any) {
    const businessEntityJSON =
      await usePersistenceStore().loadLocalBusinessEntity(businessEntityId);

    if (typeof businessEntityJSON.businessEntity === 'string') {
      return JSON.parse(businessEntityJSON.businessEntity);
    }

    return businessEntityJSON.businessEntity;
  }

  setBusinessEntityList(businessEntityMap: BusinessEntityMap) {
    const businessEntity = businessEntityMap.values().next().value;
    if (!(businessEntity instanceof BusinessEntity)) {
      console.error(
        'setBusinessEntityList: First entity is not of type BusinessEntity'
      );
      return;
    }
    this._businessEntities.set(businessEntity.id, businessEntityMap);
  }

  removeBusinessEntityFromList(businessEntityId: id) {
    this._businessEntities.delete(businessEntityId);
  }

  setBusinessEntityMap(newBusinessEntityMap: BusinessEntityMap) {
    const storeEntitiesMap: Record<string, Entity[]> = {};
    newBusinessEntityMap.forEach((entity: Entity) => {
      if (!storeEntitiesMap[entity.type]) {
        storeEntitiesMap[entity.type] = [];
      }
      storeEntitiesMap[entity.type].push(entity);
    });

    Object.entries(storeEntitiesMap).forEach(([type, entities]) => {
      const store = this.getStores().storeList[type];
      if (!store.addEntities)
        throw new Error('Please add addEntities method to store -> ' + type);
      store.addEntities(entities);
    });
  }

  /*
   * Aims to sync entity map with businessEntities Array
   * to persist in localstorage
   */
  syncBusinessEntities() {
    if (
      this._activeBusinessEntityId &&
      this._businessEntities.get(this._activeBusinessEntityId)
    ) {
      const businessEntity = this.businessEntityMap;
      this._businessEntities.set(this._activeBusinessEntityId, businessEntity);
    }
  }

  getStores() {
    const layoutStore = useLayoutStore();
    const businessEntityStore = useBusinessEntityStore();
    const roomStore = useRoomStore();
    const roomGroupStore = useRoomGroupStore();
    const deviceStore = useDeviceStore();
    const inspectionDeviceStore = useInspectionDeviceStore();
    const buildingStore = useBuildingStore();
    const inspectedBuildingStore = useInspectedBuildingStore();
    const radiatorStore = useRadiatorStore();
    const notificationStore = useNotificationStore();
    const formStore = useFormStore();
    const imagesStore = useImagesStore();
    const installationPointStore = useInstallationPointStore();
    const inspectionPointStore = useInspectionPointStore();
    const plantStore = usePlantStore();
    const orderStore = useOrderStore();

    const storeList: any = {
      businessEntity: businessEntityStore,
      room: roomStore,
      roomgroup: roomGroupStore,
      device: deviceStore,
      inspectionDevice: inspectionDeviceStore,
      building: buildingStore,
      inspectedBuilding: inspectedBuildingStore,
      radiator: radiatorStore,
      plant: plantStore,
      installationPoint: installationPointStore,
      inspectionPoint: inspectionPointStore,
    };

    return {
      businessEntityStore,
      roomStore,
      roomGroupStore,
      buildingStore,
      inspectedBuildingStore,
      deviceStore,
      inspectionDeviceStore,
      radiatorStore,
      storeList,
      plantStore,
      layoutStore,
      notificationStore,
      formStore,
      imagesStore,
      installationPointStore,
      inspectionPointStore,
      orderStore,
    };
  }

  getEntityById(id: string) {
    const entity = this.businessEntityMap.get(id);
    if (!entity) {
      throw new Error('Entity not found');
    }
    return entity;
  }

  hasEntityById(id: string): boolean {
    return this.businessEntityMap.has(id);
  }

  getAllEntitiesByParentId(id: string) {
    const result: Array<Entity> = [];
    this.businessEntityMap.forEach((value) => {
      if (value.getParentId && value.getParentId() === id) {
        result.push(value);
      }
    });
    return result;
  }

  getAllDescendants(entity: Entity): Entity[] {
    const descendants: Entity[] = [];
    const queue: Entity[] = [entity];

    while (queue.length > 0) {
      const current = queue.shift()!;
      let children: Entity[];

      if (current.type === entityTypes.roomgroup) {
        children = this.getStores().roomStore.getRoomsByRoomGroupId(
          current.id as string
        );
      } else {
        children = this.getAllEntitiesByParentId(current.id as string);
      }

      descendants.push(...children);
      queue.push(...children);
    }

    return descendants;
  }

  getAllEntitiesByCriteria(predicate: any) {
    return getAllEntitiesByCriteria(this.businessEntityMap, predicate);
  }

  getParentOfEntity(id: string) {
    const entity = this.getEntityById(id) as Entity;
    if (entity?.type === entityTypes.businessEntity) {
      return undefined;
    }

    if (entity.type === entityTypes.installationPoint) {
      return this.getEntityById(entity.getParentId() as string);
    }

    if (entity.type === entityTypes.roomgroup) {
      const firstRoom = this.getStores().roomStore.getFirstRoomOfRoomgroup(
        entity.id as string
      );

      const buildingId = this.getStores().buildingStore.buildingId;
      const hasParentRoomId = firstRoom?.parentRoomId;
      if (!hasParentRoomId && buildingId) {
        return this.getEntityById(buildingId);
      }
      return this.getEntityById(firstRoom?.getParentId() as string);
    }

    const parentId = entity.getParentId() as string;
    const parent = this.businessEntityMap.get(parentId);
    if (!parent) {
      return undefined;
    }
    if (parent?.type === entityTypes.roomgroup) {
      return this.businessEntityMap.get(parent.getParentId() as string);
    }
    return parent;
  }

  getSiblings(entity: Entity) {
    if (entity.type === entityTypes.roomgroup && entity instanceof Roomgroup) {
      const firstRoom = this.getStores().roomStore.getFirstRoomOfRoomgroup(
        entity.id
      );

      const rooms = this.getAllEntitiesByCriteria(
        (node: any) =>
          node.type === entityTypes.room &&
          node.floorLevel.type === firstRoom?.floorLevel.type &&
          node.floorLevel.level === firstRoom?.floorLevel.level &&
          node.roomGroupId !== entity.id
      );

      const roomGroups: Array<Roomgroup> = [];

      rooms.forEach((room) => {
        const roomGroup = this.businessEntityMap.get(
          room.roomGroupId
        ) as Roomgroup;
        if (
          roomGroup &&
          roomGroup.usageType !== usageTypeList[2].value &&
          roomGroup.buildingId === entity.buildingId
        ) {
          roomGroups.push(roomGroup);
        }
      });

      return roomGroups;
    }

    if (entity.type === entityTypes.room && entity instanceof Room) {
      const roomParent = this.getParentOfEntity(entity.id) as Room;
      if (roomParent.roomGroupId !== entity.roomGroupId) {
        return [entity];
      }
    }

    const parent = this.getParentOfEntity(entity.id);

    return this.getAllEntitiesByCriteria(
      (node: any) =>
        node.getParentId() === parent?.id &&
        node.type === entity.type &&
        node.id !== entity.id
    );
  }

  deleteEntity(entity: Entity, notification?: Notification) {
    if (!entity.id) {
      throw new Error(`Cannot delete entity without a valid id`);
    }

    console.debug('EntitiesStore.deleteEntity', {
      entityType: entity.type,
      entityId: entity.id,
    });

    this.trackEntityCrudEvent(entity, EntityTransactionType.DELETE_ENTITY);

    const allDescendants = this.getAllDescendants(entity);
    const entitiesToDelete = [...allDescendants, entity];

    if (!this.getStores().formStore?.currentForm?.name?.includes('List')) {
      const parent = this.getParentOfEntity(entity.id);
      if (parent) {
        this.getStores().layoutStore.changeCurrentNode(parent);
      }
    }
    if (notification) {
      this.getStores().notificationStore.addNotification(notification);
    }

    this.updateOrdinals(entity);

    for (const e of entitiesToDelete) {
      this.deleteEntityFromStore(e);
      this.getStores().orderStore.handleDeleteEntityTransactions(e);
    }

    this.syncBusinessEntities();

    if (useOrderStore().isOnSiteInspection() && entity instanceof Roomgroup) {
      this.updateSystemSRTs(entity);
    }
    if (
      useOrderStore().isOnSiteInspection() &&
      entity instanceof InspectionPoint
    ) {
      this.updateSystemAdapters(entity);
    }

    const isRoomGroupFinished =
      this.getStores().roomGroupStore.isRoomGroupFinished(
        this.getStores().roomGroupStore.activeRoomGroupId
      );
    if (!isRoomGroupFinished) {
      this.getStores().roomGroupStore.removeRoomGroupProgress();
    }
  }

  private deleteEntityFromStore(entity: Entity) {
    const store = this.getStores().storeList[entity.type];
    store.deleteEntity(entity);
    usePersistenceStore().deleteLocalEntity(entity);
  }

  async saveEntity(
    entity: Entity,
    notification?: Notification,
    payload?: EntityCrudPayload
  ) {
    if (!entity.id) {
      throw new Error(`Cannot save entity without a valid id`);
    }
    this.getStores().orderStore.handleSaveEntityTransactions(entity);

    const transaction =
      useOrderStore().activeOrder?.changes?.transactions[entity.id];

    if (
      transaction?.type === EntityTransactionType.CREATE_ENTITY ||
      transaction?.type === EntityTransactionType.UPDATE_ENTITY
    ) {
      this.trackEntityCrudEvent(entity, transaction.type, payload);
    }
    const store = this.getStores().storeList[entity.type];
    store.addEntity(entity);

    if (notification) {
      this.getStores().notificationStore.addNotification(notification);
    }

    usePersistenceStore().saveEntityToLocal(entity);
    this.syncBusinessEntities();

    if (useOrderStore().isOnSiteInspection() && entity instanceof Roomgroup) {
      this.updateSystemSRTs(entity);
    }
    if (
      useOrderStore().isOnSiteInspection() &&
      entity instanceof InspectionPoint
    ) {
      this.updateSystemAdapters(entity);
    }
    const isRoomGroupFinished =
      this.getStores().roomGroupStore.isRoomGroupFinished(
        this.getStores().roomGroupStore.activeRoomGroupId
      );
    if (!isRoomGroupFinished) {
      this.getStores().roomGroupStore.removeRoomGroupProgress();
    }
  }

  updateSystemSRTs(roomGroup: Roomgroup) {
    const SRT_CUSTOMIZED_NUMBER = '42001';
    const buildingId = roomGroup?.buildingId;
    if (!buildingId) return;

    const inspectedBuilding =
      this.getStores().inspectedBuildingStore.getInspectedBuildingByBuildingId(
        buildingId
      );

    if (!inspectedBuilding) return;

    const systemSrtCount =
      this.getStores().inspectedBuildingStore.getSystemSRTs(buildingId);

    this.upsertEstimation(inspectedBuilding, {
      type: 'SRT',
      customizedNumber: SRT_CUSTOMIZED_NUMBER,
      count: systemSrtCount,
      estimation: this.findExistingEstimation(
        inspectedBuilding,
        SRT_CUSTOMIZED_NUMBER
      )?.estimation,
    });

    usePersistenceStore().saveEntityToLocal(inspectedBuilding);
  }

  updateSystemAdapters(inspectionPoint: InspectionPoint) {
    const room = this.getEntityById(inspectionPoint.roomId);
    if (!room) return;

    const roomGroup = this.getEntityById(room.roomGroupId);
    if (!roomGroup) return;

    const inspectedBuilding =
      this.getStores().inspectedBuildingStore.getInspectedBuildingByBuildingId(
        roomGroup.buildingId
      );

    const buildingId = inspectedBuilding?.buildingId;
    if (!buildingId || !inspectedBuilding) return;

    const adapterCounts = this.collectAdapterCounts(buildingId);

    for (const [customizedNumber, count] of adapterCounts) {
      const existing = this.findExistingEstimation(
        inspectedBuilding,
        customizedNumber
      );

      this.upsertEstimation(inspectedBuilding, {
        id: existing?.id,
        type: ProductEstimationTypesEnmu.ADAPTER,
        customizedNumber,
        count,
        estimation: existing?.estimation,
      });
    }

    const currentNumbers = new Set(adapterCounts.keys());
    const list = inspectedBuilding.productEstimations;

    for (let i = list.length - 1; i >= 0; i--) {
      const pe = list[i];
      if (
        pe?.type === ProductEstimationTypesEnmu.ADAPTER &&
        !currentNumbers.has(pe.customizedNumber)
      ) {
        list.splice(i, 1);
      }
    }

    usePersistenceStore().saveEntityToLocal(inspectedBuilding);
  }

  findExistingEstimation(
    inspectedBuilding: { productEstimations?: ProductEstimation[] },
    customizedNumber: string
  ): ProductEstimation | undefined {
    return inspectedBuilding.productEstimations?.find(
      (e) => e.customizedNumber === customizedNumber
    );
  }

  upsertEstimation(
    inspectedBuilding: { productEstimations: ProductEstimation[] },
    data: ProductEstimation
  ): void {
    const list =
      inspectedBuilding.productEstimations ??
      (inspectedBuilding.productEstimations = []);
    const idx = list.findIndex(
      (e) => e.customizedNumber === data.customizedNumber
    );

    const id =
      data.id ??
      this.findExistingEstimation(inspectedBuilding, data.customizedNumber)
        ?.id ??
      generateUniqueId();

    const entry = new ProductEstimation({
      id,
      type: data.type,
      customizedNumber: data.customizedNumber,
      count: data.count,
      estimation: data.estimation,
    });

    if (idx === -1) list.push(entry);
    else list.splice(idx, 1, entry);
  }

  collectAdapterCounts(buildingId: string): Map<string, number> {
    const roomGroupStore = useRoomGroupStore();
    const inspectionPointStore = useInspectionPointStore();

    const roomGroups =
      roomGroupStore.getRoomGroupsByBuildingId(buildingId) ?? [];
    const counts = new Map<string, number>();

    for (const rg of roomGroups) {
      const ips =
        inspectionPointStore.getInspectionPointsByRoomGroupId(rg.id) ?? [];
      for (const ip of ips) {
        const accessories = ip?.accessories ?? [];
        for (const a of accessories) {
          const key = a?.customizedNumber;
          if (!key) continue;
          counts.set(key, (counts.get(key) ?? 0) + 1);
        }
      }
    }

    return counts;
  }

  updateOrdinals(entity: Entity) {
    const siblings = this.getSiblings(entity);
    siblings.sort((a, b) => a.ordinal - b.ordinal);
    siblings.forEach((sibling, index) => {
      sibling.ordinal = index;
      usePersistenceStore().saveEntityToLocal(sibling);
    });

    this.syncBusinessEntities();
  }

  resetMiniEntityStores() {
    const miniEntityStores: Array<miniEntityStore> = Object.values(
      this.getStores().storeList
    );

    miniEntityStores.forEach((store) => {
      store.resetState();
    });
  }
}
